// MARK:- Compact "Add your lid" compatible-lids block — atomic bucket+lid cart add
// Revision 1 (docs/specs/compatible-lids/revision-1.md): live-total overlay (never races the frozen
// Dynamic Volume Pricing writer), cart-aware tier math, color-matched lid variants, Shopify standard
// storefront events (shopify:cart:lines-update / shopify:cart:error) dispatched from the atomic add.
(function () {
  'use strict';

  const TOTAL_SELECTOR = '[data-epack-lids-total]';
  const ACTIVE_CLASS = 'epack-lids-active';

  class EpackCompatibleLids extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this._busy = false;
      this._activated = false;
      this._recomputeTimer = null;
      this._recomputeToken = 0;
      this._rowVariants = new WeakMap();

      this._colorOptionIndex = parseInt(this.dataset.colorOptionIndex, 10) || null;
      this._currentColor = (this.dataset.currentColor || '').toLowerCase();

      document.addEventListener('submit', this._onDocumentSubmit.bind(this), { capture: true, signal });

      this.addEventListener('change', (event) => {
        if (event.target.matches('.epack-compatible-lids__checkbox')) {
          this._onCheckboxChange(event.target);
          this._scheduleRecompute();
        } else if (event.target.matches('.epack-compatible-lids__qty-input')) {
          this._onLidQtyChange(event.target);
          this._updateQtySummary(event.target.closest('[data-lid-row]'));
          this._scheduleRecompute();
        }
      }, { signal });

      this.addEventListener('input', (event) => {
        if (event.target.matches('.epack-compatible-lids__qty-input')) {
          this._updateQtySummary(event.target.closest('[data-lid-row]'));
          this._scheduleRecompute(200);
        }
      }, { signal });

      // The bucket's own quantity input lives outside the <form> (associated via a `form`
      // attribute), so it's reached by document-level delegation rather than a direct reference —
      // the same pattern the frozen Dynamic Volume Pricing script uses for the same reason.
      const formId = this.getAttribute('form-id');
      document.addEventListener('change', (event) => {
        if (event.target.matches('.quantity-selector__input') && event.target.getAttribute('form') === formId) {
          this._onBucketQtyChange();
          this._scheduleRecompute(80);
        }
      }, { signal });
      document.addEventListener('input', (event) => {
        if (event.target.matches('.quantity-selector__input') && event.target.getAttribute('form') === formId) {
          this._onBucketQtyChange();
          this._scheduleRecompute(350);
        }
      }, { signal });

      // Variant switch: re-resolve lid colors and re-establish the total overlay after the
      // buy-buttons partial rerender. NOTE: filter on event.target's id attribute — detail.formId is
      // form.id, which on this form resolves to the hidden input *named* "id" (an element, not a string).
      document.addEventListener('variant:changed', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLFormElement) || target.getAttribute('id') !== formId) return;
        this._onVariantChanged(event.detail?.variant);
        this._scheduleRecompute();
      }, { signal });

      this._applyColorToRows();
    }

    disconnectedCallback() {
      this.abortController.abort();
      clearTimeout(this._recomputeTimer);
      const form = document.getElementById(this.getAttribute('form-id'));
      if (form) this._deactivateOverlay(form);
    }

    _checkedBoxes() {
      return Array.from(this.querySelectorAll('.epack-compatible-lids__checkbox:checked'));
    }

    _currentBucketQty() {
      const formId = this.getAttribute('form-id');
      const input = document.querySelector(`.quantity-selector__input[form="${formId}"]`);
      return input ? (parseInt(input.value, 10) || 1) : 1;
    }

    /* ------------------------------------------------------------------ quantity mirroring */

    _onCheckboxChange(checkbox) {
      const row = checkbox.closest('[data-lid-row]');
      const qtyInput = row?.querySelector('.epack-compatible-lids__qty-input');
      if (!qtyInput) return;
      delete checkbox.dataset.qtyDirty;
      if (checkbox.checked) {
        qtyInput.value = this._currentBucketQty();
        this._updateQtySummary(row);
      } else {
        this._collapseQtyDetail(row);
        this._updateCoverageWarning(row, parseInt(qtyInput.value, 10) || 1);
      }
    }

    _onLidQtyChange(qtyInput) {
      const row = qtyInput.closest('[data-lid-row]');
      const checkbox = row?.querySelector('.epack-compatible-lids__checkbox');
      if (checkbox) checkbox.dataset.qtyDirty = 'true';
    }

    _onBucketQtyChange() {
      const bucketQty = this._currentBucketQty();
      this._checkedBoxes().forEach((checkbox) => {
        const row = checkbox.closest('[data-lid-row]');
        const qtyInput = row?.querySelector('.epack-compatible-lids__qty-input');
        if (!qtyInput) return;
        if (checkbox.dataset.qtyDirty !== 'true') qtyInput.value = bucketQty;
        // Dirty rows keep their own quantity but still refresh the summary + coverage warning
        this._updateQtySummary(row);
      });
    }

    _updateQtySummary(row) {
      const summary = row?.querySelector('[data-qty-summary]');
      const qtyInput = row?.querySelector('.epack-compatible-lids__qty-input');
      if (!summary || !qtyInput) return;
      const qty = parseInt(qtyInput.value, 10) || 1;
      const countEl = summary.querySelector('[data-qty-summary-count]');
      const unitEl = summary.querySelector('[data-qty-summary-unit]');
      if (countEl) countEl.textContent = String(qty);
      if (unitEl) unitEl.textContent = qty === 1 ? summary.dataset.unitLabel : summary.dataset.unitLabelPlural;
      this._updateCoverageWarning(row, qty);
    }

    // MARK:- coverage warning ("N buckets will not have a lid") when a checked lid's qty < bucket qty
    _updateCoverageWarning(row, lidQty) {
      const warning = row?.querySelector('[data-coverage-warning]');
      if (!warning) return;
      const checkbox = row.querySelector('.epack-compatible-lids__checkbox');
      const deficit = this._currentBucketQty() - lidQty;
      const show = Boolean(checkbox?.checked) && deficit > 0;
      if (show) {
        const template = (deficit === 1 ? this.dataset.coverageWarningOne : this.dataset.coverageWarningOther) || '';
        const textEl = warning.querySelector('[data-coverage-warning-text]');
        if (textEl) textEl.textContent = template.replace('__count__', deficit);
      }
      warning.hidden = !show;
    }

    _collapseQtyDetail(row) {
      const collapsible = row?.querySelector('.epack-compatible-lids__qty');
      const toggle = row?.querySelector('.epack-compatible-lids__adjust');
      if (collapsible) collapsible.open = false;
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    }

    /* ------------------------------------------------------------------ color matching */

    _onVariantChanged(variant) {
      if (!this._colorOptionIndex || !variant) return;
      const options = Array.isArray(variant.options) ? variant.options : [];
      const value = options[this._colorOptionIndex - 1] ?? variant[`option${this._colorOptionIndex}`] ?? '';
      this._currentColor = String(value).toLowerCase();
      this._applyColorToRows();
    }

    _rowVariantData(row) {
      if (this._rowVariants.has(row)) return this._rowVariants.get(row);
      let data = null;
      const script = row.querySelector('[data-lid-variants]');
      if (script) {
        try {
          data = JSON.parse(script.textContent);
        } catch (error) {
          data = null;
        }
      }
      this._rowVariants.set(row, data);
      return data;
    }

    // Effective variant = the lid variant in the bucket's current color, else the lid's default.
    // All data comes from the server-rendered island — no client-side product fetches.
    _resolveVariant(data) {
      if (!data) return { variant: null, matched: false };
      const byColor = data.byColor || {};
      if (this._colorOptionIndex && data.colorOptionIndex && this._currentColor && byColor[this._currentColor]) {
        return { variant: byColor[this._currentColor], matched: true };
      }
      return { variant: data.default || null, matched: false };
    }

    _applyColorToRows() {
      this.querySelectorAll('[data-lid-row]').forEach((row) => {
        const data = this._rowVariantData(row);
        if (!data) return;
        const { variant, matched } = this._resolveVariant(data);
        const checkbox = row.querySelector('.epack-compatible-lids__checkbox');
        if (!checkbox || !variant) return;

        checkbox.dataset.variantId = String(variant.id);

        // Sold-out state is per resolved variant
        const soldOut = variant.available === false;
        row.classList.toggle('epack-compatible-lids__item--sold-out', soldOut);
        checkbox.disabled = soldOut;
        if (soldOut && checkbox.checked) {
          checkbox.checked = false;
          this._onCheckboxChange(checkbox);
          this._scheduleRecompute();
        }
        const soldOutLabel = row.querySelector('[data-sold-out-label]');
        const price = row.querySelector('[data-lid-price]');
        if (soldOutLabel) soldOutLabel.hidden = !soldOut;
        if (price) price.hidden = soldOut;

        // "in {color}" note when the lid has no variant in the bucket's color
        const note = row.querySelector('[data-color-note]');
        const showNote = Boolean(this._colorOptionIndex && this._currentColor && data.colorOptionIndex && !matched && variant.color);
        if (note) {
          note.textContent = showNote ? (this.dataset.colorNoteTemplate || '__color__').replace('__color__', variant.color) : '';
          note.hidden = !showNote;
        }

        this._swapThumbnail(row, variant.image);

        const describedBy = [
          checkbox.dataset.subtitleId,
          showNote && note ? note.id : '',
          soldOut && soldOutLabel ? soldOutLabel.id : '',
        ].filter(Boolean).join(' ');
        if (describedBy) checkbox.setAttribute('aria-describedby', describedBy);
        else checkbox.removeAttribute('aria-describedby');
      });
    }

    // Variant image present → show it; null → restore the server-rendered thumbnail. Rendered size is
    // fixed at 48px in CSS so the swap is CLS-safe.
    _swapThumbnail(row, image) {
      const img = row.querySelector('.epack-compatible-lids__image');
      if (!img) return;
      if (!img.dataset.originalSrc) {
        img.dataset.originalSrc = img.getAttribute('src') || '';
        img.dataset.originalSrcset = img.getAttribute('srcset') || '';
        img.dataset.originalAlt = img.getAttribute('alt') || '';
        img.dataset.originalWidth = img.getAttribute('width') || '';
        img.dataset.originalHeight = img.getAttribute('height') || '';
      }
      if (image && image.src) {
        if (img.getAttribute('src') === image.src) return;
        img.removeAttribute('srcset');
        img.src = image.src;
        if (image.width) img.width = image.width;
        if (image.height) img.height = image.height;
        img.alt = image.alt || '';
      } else if (img.getAttribute('src') !== img.dataset.originalSrc) {
        img.src = img.dataset.originalSrc;
        if (img.dataset.originalSrcset) img.setAttribute('srcset', img.dataset.originalSrcset);
        if (img.dataset.originalWidth) img.setAttribute('width', img.dataset.originalWidth);
        if (img.dataset.originalHeight) img.setAttribute('height', img.dataset.originalHeight);
        img.alt = img.dataset.originalAlt;
      }
    }

    /* ------------------------------------------------------------------ live total */

    _scheduleRecompute(delay = 60) {
      clearTimeout(this._recomputeTimer);
      const form = document.getElementById(this.getAttribute('form-id'));
      if (!form) return;
      this._recomputeTimer = setTimeout(() => this._recomputeTotal(form), delay);
    }

    // Tier select mirrors the frozen `updatePrice()` in theme.bundle.js exactly: the highest break
    // where (in-cart qty of that variant + qty) >= minimumQuantity, then unit price × qty.
    _tierUnitPrice(pricingData, quantity, cartQuantity) {
      let unitPrice = pricingData.basePrice;
      const totalQuantity = cartQuantity + quantity;
      if (Array.isArray(pricingData.breaks)) {
        for (let i = pricingData.breaks.length - 1; i >= 0; i--) {
          if (totalQuantity >= pricingData.breaks[i].minimumQuantity) {
            unitPrice = pricingData.breaks[i].price;
            break;
          }
        }
      }
      return unitPrice;
    }

    _cartQuantity(cart, variantId) {
      if (!cart?.items) return 0;
      const id = parseInt(variantId, 10);
      return cart.items.reduce((total, item) => (item.variant_id === id ? total + item.quantity : total), 0);
    }

    async _recomputeTotal(form) {
      const checked = this._checkedBoxes();
      if (checked.length === 0) {
        this._activated = false;
        this._deactivateOverlay(form);
        return;
      }
      this._activated = true;

      const bucketPricingScript = form.querySelector('.product-form__add-button [data-pricing-data]') || form.querySelector('[data-pricing-data]');
      if (!bucketPricingScript) return;
      let bucketPricing;
      try {
        bucketPricing = JSON.parse(bucketPricingScript.textContent);
      } catch (error) {
        return;
      }

      // Monotonic token: an older /cart.js response can never overwrite a newer qty/color state.
      const token = ++this._recomputeToken;
      let cart = { items: [] };
      try {
        const response = await fetch(`${window.themeVariables.routes.cartUrl}.js`);
        cart = await response.json();
      } catch (error) {
        cart = { items: [] };
      }
      if (token !== this._recomputeToken || !this.isConnected) return;

      const stillChecked = this._checkedBoxes();
      if (stillChecked.length === 0) {
        this._activated = false;
        this._deactivateOverlay(form);
        return;
      }

      const bucketQty = this._currentBucketQty();
      const bucketVariantId = bucketPricing.variantId ?? new FormData(form).get('id');
      let total = this._tierUnitPrice(bucketPricing, bucketQty, this._cartQuantity(cart, bucketVariantId)) * bucketQty;

      stillChecked.forEach((checkbox) => {
        const row = checkbox.closest('[data-lid-row]');
        const pricingScript = row?.querySelector('[data-lid-pricing-data]');
        const qtyInput = row?.querySelector('.epack-compatible-lids__qty-input');
        if (!pricingScript || !qtyInput) return;
        let lidPricing;
        try {
          lidPricing = JSON.parse(pricingScript.textContent);
        } catch (error) {
          return;
        }
        const qty = parseInt(qtyInput.value, 10) || 1;
        total += this._tierUnitPrice(lidPricing, qty, this._cartQuantity(cart, checkbox.dataset.variantId)) * qty;
      });

      this._writeTotal(form, total, bucketPricing);
    }

    // Overlay, not a rewrite: a sibling span after the native [data-price-element] (hidden via CSS
    // while the form carries ACTIVE_CLASS). The frozen script keeps writing its hidden span harmlessly.
    _writeTotal(form, totalCents, pricingData) {
      const priceElement = form.querySelector('.product-form__add-button [data-price-element]');
      if (!priceElement) return;
      let overlay = priceElement.parentElement.querySelector(TOTAL_SELECTOR);
      if (!overlay) {
        overlay = document.createElement('span');
        overlay.className = priceElement.className;
        overlay.setAttribute('data-epack-lids-total', '');
        priceElement.insertAdjacentElement('afterend', overlay);
      }
      const visuallyHidden = priceElement.querySelector('.visually-hidden');
      let formatted = typeof window.formatMoney === 'function' ? window.formatMoney(totalCents) : (totalCents / 100).toFixed(2);
      if (pricingData.currencyEnabled && pricingData.currencyCode) {
        formatted = `${formatted} ${pricingData.currencyCode}`;
      }
      overlay.innerHTML = visuallyHidden ? visuallyHidden.outerHTML + formatted : formatted;
      form.classList.add(ACTIVE_CLASS);
    }

    _deactivateOverlay(form) {
      form.classList.remove(ACTIVE_CLASS);
      form.querySelectorAll(TOTAL_SELECTOR).forEach((el) => el.remove());
    }

    _resetForm(form) {
      this._checkedBoxes().forEach((checkbox) => {
        checkbox.checked = false;
        delete checkbox.dataset.qtyDirty;
        const row = checkbox.closest('[data-lid-row]');
        const qtyInput = row?.querySelector('.epack-compatible-lids__qty-input');
        if (qtyInput) qtyInput.value = 1;
        this._collapseQtyDetail(row);
      });
      this._activated = false;
      this._recomputeToken++;
      clearTimeout(this._recomputeTimer);
      this._deactivateOverlay(form);
      this._setError('');
    }

    /* ------------------------------------------------------------------ standard storefront events */

    // Guarded: the shim (layout/theme.liquid) may not have loaded yet or may be blocked. Event
    // dispatch must never break the add itself.
    _standardEvents() {
      const lib = window.StandardEvents;
      return lib && lib.CartLinesUpdateEvent ? lib : null;
    }

    _dispatchLinesUpdate(items, detail) {
      const lib = this._standardEvents();
      if (!lib) return null;
      try {
        const deferred = lib.CartLinesUpdateEvent.createPromise();
        // Swallow unhandled rejections when no listener consumed the promise
        deferred.promise.catch(() => {});
        this.dispatchEvent(new lib.CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity })),
          promise: deferred.promise,
          detail,
        }));
        return deferred;
      } catch (error) {
        return null;
      }
    }

    _resolveLinesUpdate(deferred, { cart = null, userErrors = [], detail = {} } = {}) {
      if (!deferred) return;
      try {
        const lib = this._standardEvents();
        const standardCart = cart && lib?.CartLinesUpdateEvent?.createCartFromAjaxResponse
          ? lib.CartLinesUpdateEvent.createCartFromAjaxResponse(cart)
          : null;
        deferred.resolve({ cart: standardCart, userErrors, warnings: [], detail });
      } catch (error) {
        // never let reporting break the add
      }
    }

    _rejectLinesUpdate(deferred, error, message) {
      const lib = this._standardEvents();
      try {
        if (lib?.CartErrorEvent) {
          this.dispatchEvent(new lib.CartErrorEvent({ error: message, code: 'SERVICE_UNAVAILABLE' }));
        }
        if (deferred) deferred.reject(error);
      } catch (reportError) {
        // never let reporting break the add
      }
    }

    /* ------------------------------------------------------------------ atomic add */

    _onDocumentSubmit(event) {
      const form = event.target;
      // NOTE: form.id can resolve to a named form control (this form has an `id`-named
      // hidden input for the variant id) instead of the form element's own id attribute —
      // use getAttribute to sidestep that shadowing.
      if (!(form instanceof HTMLFormElement) || form.getAttribute('id') !== this.getAttribute('form-id')) {
        return;
      }

      if (this._busy) {
        // An atomic add is in flight — swallow the submit so the native ProductForm can't fire a bucket-only add.
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const checked = this._checkedBoxes();
      if (checked.length === 0) {
        // Nothing checked — let the native ProductForm flow run untouched.
        return;
      }

      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      this._submitAtomicAdd(form, checked);
    }

    async _submitAtomicAdd(form, checked) {
      this._busy = true;
      this._setError('');

      const submitButtons = Array.from(form.elements).filter((el) => el.type === 'submit');
      submitButtons.forEach((button) => {
        button.setAttribute('disabled', 'disabled');
        button.setAttribute('aria-busy', 'true');
      });

      let deferred = null;
      const errorMessage = this.dataset.addErrorMessage || '';

      try {
        const formData = new FormData(form);
        const bucketVariantId = formData.get('id');
        const bucketQuantity = parseInt(formData.get('quantity'), 10) || 1;
        const sellingPlan = formData.get('selling_plan');

        const bucketProperties = {};
        formData.forEach((value, key) => {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match) {
            bucketProperties[match[1]] = value;
          }
        });

        const lidSet = String(Date.now());
        const items = [
          {
            id: bucketVariantId,
            quantity: bucketQuantity,
            ...(Object.keys(bucketProperties).length ? { properties: bucketProperties } : {}),
            ...(sellingPlan ? { selling_plan: sellingPlan } : {}),
          },
        ];

        checked.forEach((checkbox) => {
          const row = checkbox.closest('[data-lid-row]');
          const qtyInput = row?.querySelector('.epack-compatible-lids__qty-input');
          const lidQuantity = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : bucketQuantity;
          items.push({
            id: checkbox.dataset.variantId,
            quantity: lidQuantity,
            properties: {
              _epack_lid_for: bucketVariantId,
              _epack_lid_set: lidSet,
              // MARK:- hidden origin attribution (metaobject origin field; the snippet guarantees a
              // fallback value, so this is always present — the guard is only belt-and-braces)
              ...(this.dataset.origin ? { _origin: this.dataset.origin } : {}),
            },
          });
        });

        const eventDetail = { source: 'epack-compatible-lids', lidFor: bucketVariantId, lidSet };
        deferred = this._dispatchLinesUpdate(items, eventDetail);

        const response = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items }),
        });

        const responseJson = await response.json();

        if (!response.ok) {
          // Cart declined the change (e.g. sold out): a user error, not an error event.
          const message = responseJson.description || errorMessage;
          this._resolveLinesUpdate(deferred, {
            userErrors: [{ code: 'INVALID', field: null, message }],
            detail: eventDetail,
          });
          this._setError(message);
          form.dispatchEvent(new CustomEvent('cart-notification:show', {
            bubbles: true,
            cancelable: true,
            detail: { status: 'error', error: message },
          }));
          return;
        }

        if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
          // Standard-events contract: the promise resolves with the post-mutation cart. This path has
          // no other cart fetch to reuse, so fetch it here — but never let a failure block the redirect.
          try {
            const cartResponse = await fetch(`${window.themeVariables.routes.cartUrl}.js`);
            const cartContent = await cartResponse.json();
            this._resolveLinesUpdate(deferred, { cart: cartContent, detail: eventDetail });
          } catch (error) {
            this._resolveLinesUpdate(deferred, { detail: eventDetail });
          }
          window.location.href = `${window.Shopify.routes.root}cart`;
          return;
        }

        form.dispatchEvent(new CustomEvent('variant:added', {
          bubbles: true,
          detail: { variant: responseJson.items ? responseJson.items[0] : responseJson },
        }));

        const cartResponse = await fetch(`${window.themeVariables.routes.cartUrl}.js`);
        const cartContent = await cartResponse.json();

        // Reuse the post-add cart for the standard event — no extra fetch for reporting
        this._resolveLinesUpdate(deferred, { cart: cartContent, detail: eventDetail });
        deferred = null;

        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: cartContent },
        }));

        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart: cartContent,
            openMiniCart: window.themeVariables.settings.cartType === 'drawer' && form.closest('.drawer') === null,
          },
        }));

        this.dispatchEvent(new CustomEvent('epack:compatible-lids:added', {
          bubbles: true,
          detail: { items },
        }));

        this._resetForm(form);

        form.dispatchEvent(new CustomEvent('cart-notification:show', {
          bubbles: true,
          cancelable: true,
          detail: { status: 'success', error: '' },
        }));
      } catch (error) {
        this._rejectLinesUpdate(deferred, error, errorMessage);
        this._setError(errorMessage);
        form.dispatchEvent(new CustomEvent('cart-notification:show', {
          bubbles: true,
          cancelable: true,
          detail: { status: 'error', error: errorMessage },
        }));
      } finally {
        this._busy = false;
        submitButtons.forEach((button) => {
          button.removeAttribute('disabled');
          button.removeAttribute('aria-busy');
        });
      }
    }

    _setError(message) {
      const errorEl = this.querySelector('.epack-compatible-lids__error');
      if (!errorEl) return;
      errorEl.textContent = message || '';
      errorEl.hidden = !message;
    }
  }

  if (!customElements.get('epack-compatible-lids')) {
    customElements.define('epack-compatible-lids', EpackCompatibleLids);
  }
})();
